/**
 * Route-scoped error boundary.
 *
 * Wraps a route so an unhandled render error shows a diagnostic panel
 * instead of a blank screen. Preserves the sidebar / header (parent
 * layout renders normally; only the boundary's children are replaced).
 *
 * Added 2026-07-24 after Jacob's feedback bug: Calendar page went blank
 * on click for one user and we had no way to see the error without a
 * ScreenConnect + DevTools session on his laptop.
 */

import { Component, ReactNode, ErrorInfo } from 'react';

interface Props {
  /** Name shown in the error UI, e.g. "Calendar" */
  routeName: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console — techs on the desktop can open DevTools to see it.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary/${this.props.routeName}]`, error, info);
    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">⚠️</span>
            <div>
              <h2 className="text-lg font-semibold text-red-200">
                {this.props.routeName} failed to load
              </h2>
              <p className="text-sm text-red-300/80 mt-0.5">
                Something crashed on this page. Details below.
              </p>
            </div>
          </div>

          <div className="bg-slate-900/60 border border-slate-700 rounded p-3">
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Error</div>
            <div className="text-sm text-red-200 font-mono break-words">
              {error.name}: {error.message}
            </div>
          </div>

          {error.stack && (
            <details className="bg-slate-900/60 border border-slate-700 rounded p-3">
              <summary className="text-xs text-slate-400 uppercase tracking-wider cursor-pointer select-none">
                Stack trace
              </summary>
              <pre className="mt-2 text-[11px] text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap break-words">
                {error.stack}
              </pre>
            </details>
          )}

          {info?.componentStack && (
            <details className="bg-slate-900/60 border border-slate-700 rounded p-3">
              <summary className="text-xs text-slate-400 uppercase tracking-wider cursor-pointer select-none">
                Component tree
              </summary>
              <pre className="mt-2 text-[11px] text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap break-words">
                {info.componentStack}
              </pre>
            </details>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-3 py-1.5 text-sm rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors"
            >
              Reload page
            </button>
            <div className="text-xs text-slate-500 ml-auto">
              If this keeps happening, send this whole panel via Feedback (top-right widget).
            </div>
          </div>
        </div>
      </div>
    );
  }
}
